import {
  LICENSE_LABELS,
  Product,
  type ProductLicense,
  ProductLicense as LicenseEnum,
  ProductStatus,
  canSell,
  skuOf,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import type { z } from 'zod';
import { Field, Seg } from '../../components/Form';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Overlays';
import { api, newIdempotencyKey } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import { useCategories, useInvalidateProducts } from '../../lib/products-api';

const EditableStatus = ProductStatus.exclude(['archived']);

/** "Novo produto" (board Produtos). */
export function NewProductDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (p: Product) => void;
}) {
  const categories = useCategories();
  const invalidate = useInvalidateProducts();
  const [key] = useState(newIdempotencyKey);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('Casa e decoração');
  const [status, setStatus] = useState<z.infer<typeof EditableStatus>>('idea');
  const [origin, setOrigin] = useState<'own' | 'third_party'>('own');
  const [sourceUrl, setSourceUrl] = useState('');
  const [license, setLicense] = useState<ProductLicense>('unknown');
  const [error, setError] = useState<string | null>(null);
  const sellable = canSell({ origin, license });

  const create = useMutation({
    mutationFn: () =>
      api.post(
        '/products',
        Product,
        {
          name: name.trim(),
          category: category.trim() || null,
          status: !sellable && status === 'active' ? 'prototype' : status,
          origin,
          sourceUrl: origin === 'third_party' ? sourceUrl.trim() || null : null,
          license: origin === 'third_party' ? license : null,
        },
        { idempotencyKey: key },
      ),
    onSuccess: (p) => {
      invalidate();
      onCreated(p);
    },
    onError: (err) => setError(errorMessage(err)),
  });

  return (
    <Modal
      title="Novo produto"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={create.isPending}
            onClick={() => {
              if (!name.trim()) {
                setError('Dê um nome ao produto.');
                return;
              }
              create.mutate();
            }}
          >
            Criar produto
          </button>
        </>
      }
    >
      <Field
        id="np-name"
        label="Nome"
        error={error === 'Dê um nome ao produto.' ? error : null}
        help={`SKU sugerido: ${skuOf(name) || '—'}`}
      >
        <input
          id="np-name"
          className={error === 'Dê um nome ao produto.' ? 'input err' : 'input'}
          placeholder="Ex.: Vaso Espiral"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
        />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field id="np-cat" label="Categoria">
          <input
            id="np-cat"
            className="input"
            list="np-cats"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />
          <datalist id="np-cats">
            {(categories.data?.data ?? []).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </datalist>
        </Field>
        <Field id="np-status" label="Situação">
          <select
            id="np-status"
            className="input"
            value={status}
            onChange={(e) => setStatus(EditableStatus.parse(e.target.value))}
          >
            <option value="idea">Ideia</option>
            <option value="prototype">Protótipo</option>
            <option value="active" disabled={!sellable}>
              Ativo
            </option>
          </select>
        </Field>
      </div>
      <Field label="Origem do projeto">
        <Seg
          label="Origem"
          value={origin}
          options={[
            { value: 'own', label: 'Projeto próprio' },
            { value: 'third_party', label: 'De terceiro' },
          ]}
          onChange={setOrigin}
        />
      </Field>
      {origin === 'third_party' ? (
        <>
          <Field id="np-link" label="Link do modelo">
            <input
              id="np-link"
              className="input"
              placeholder="https://… (MakerWorld, Printables, Thingiverse)"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
            />
          </Field>
          <Field id="np-lic" label="Licença">
            <select
              id="np-lic"
              className="input"
              value={license}
              onChange={(e) => setLicense(LicenseEnum.parse(e.target.value))}
            >
              {LicenseEnum.options.map((l) => (
                <option key={l} value={l}>
                  {LICENSE_LABELS[l]}
                </option>
              ))}
            </select>
          </Field>
          {sellable ? (
            <div className="alert ok">
              <Icon name="check" />
              <div className="sm">
                Licença permite venda. Guarde o comprovante em Arquivos › Documentos.
              </div>
            </div>
          ) : (
            <div className="alert err">
              <Icon name="alert" />
              <div className="sm">
                <b>Sem licença comercial.</b> Dá para salvar como ideia ou protótipo, mas o produto
                fica bloqueado para venda até você registrar uma licença que permita vender.
              </div>
            </div>
          )}
        </>
      ) : null}
      {error && error !== 'Dê um nome ao produto.' ? <p className="help err">{error}</p> : null}
    </Modal>
  );
}
